/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { debounce } from 'lodash'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { withSmallErrorBoundary } from '@modules/cui/Boundary'
import { useGetRootByApp } from '@modules/cui/Breadcrumbs/rootCrumbBuilder'
import { useConfig } from '@modules/cui/ConfigContext'
import type { RoutableBreadcrumb } from '@modules/ui-types'

import BreadcrumbsContext from './BreadcrumbsContext'
import BreadcrumbsImpl from './BreadcrumbsImpl'
import ContentResetPortal from './ContentResetPortal'
import { ScreenReaderAnnouncements } from './ScreenReaderAnnouncements'

import type { FC } from 'react'

type Props = {
  breadcrumbs?: RoutableBreadcrumb[]
  truncate?: boolean
}

const extractBreadcrumbsText = (breadRef: HTMLDivElement | null | undefined) =>
  (breadRef?.innerText ?? '').slice(-10)

const Breadcrumbs: FC<Props> = ({ breadcrumbs, truncate }) => {
  /**
   * Since breadcrumb items contain ReactNode instead
   * of string values, it was necessary to obtain the current value
   * through _breadRef?.innerText
   * **/

  const { breadcrumbsRef, announcementsRef } = useContext(BreadcrumbsContext)
  const appName = useConfig('APP_NAME')
  const appType = useConfig('CLOUD_UI_APP')

  const [breadKey, setBreadKey] = useState(extractBreadcrumbsText(breadcrumbsRef?.current))

  const rootByApp = useGetRootByApp({
    isAtRoot: (breadcrumbs?.length ?? 0) === 0,
    appName,
    appType,
  })
  const allRoutableBreadcrumbs = useMemo(
    () => [...(rootByApp ?? []), ...(breadcrumbs ?? [])],
    [breadcrumbs, rootByApp],
  )

  const debouncedBreadKeyCalculation = useRef(
    debounce((_breadRef) => {
      /** "breadKey" is needed for memoization of the ScreenReaderAnnouncements
       * component so that rendering occurs only in the case of actual changes
       * in breadcrumb values. **/
      setBreadKey(extractBreadcrumbsText(_breadRef))
    }, 300),
  )

  useEffect(() => {
    const fn = debouncedBreadKeyCalculation.current
    fn(breadcrumbsRef?.current)

    return function cancelDebounce() {
      fn.cancel()
    }
  }, [allRoutableBreadcrumbs, breadcrumbsRef])

  return (
    <React.Fragment>
      {announcementsRef?.current && (
        <ContentResetPortal container={announcementsRef.current}>
          <ScreenReaderAnnouncements breadcrumbs={allRoutableBreadcrumbs} prefix={breadKey} />
        </ContentResetPortal>
      )}
      {breadcrumbsRef?.current && (
        <ContentResetPortal container={breadcrumbsRef.current}>
          <BreadcrumbsImpl breadcrumbs={allRoutableBreadcrumbs} truncate={truncate} />
        </ContentResetPortal>
      )}
    </React.Fragment>
  )
}

export default withSmallErrorBoundary(Breadcrumbs)
